import { useEffect, useState } from 'react';
import { IconPlus, IconUsers } from '../../components/icons';
import { Avatar, Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Tabs, useAsync, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { PermissionMatrix, type Grant } from './PermissionMatrix';

export function GroupsPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [selected, setSelected] = useState<number | null>(null);
  const [createModal, setCreateModal] = useState(false);

  const groups = useAsync(() => api.get('/api/groups'), []);
  const catalog = useAsync(() => api.get('/api/roles/catalog/permissions'), []);

  useEffect(() => {
    if (!selected && groups.data?.length) setSelected(groups.data[0].id);
  }, [groups.data, selected]);

  // Hierarchie aufbauen: Wurzelgruppen mit ihren Untergruppen.
  const roots = (groups.data ?? []).filter((group: any) => !group.parent_group_id);
  const childrenOf = (id: number) => (groups.data ?? []).filter((group: any) => group.parent_group_id === id);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconUsers width={22} height={22} />}
        title="Gruppenverwaltung"
        subtitle={
          <>
            Gruppen bündeln Mitarbeitende, vergeben Rollen und Objektzuständigkeiten. Untergruppen erben
            sämtliche Rollen und Rechte ihrer übergeordneten Gruppe.
          </>
        }
        pill="Administration"
        tone="accent"
        actions={
          can('admin.groups.manage') && (
            <button className="btn primary" onClick={() => setCreateModal(true)}>
              <IconPlus width={15} height={15} /> Gruppe anlegen
            </button>
          )
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        <Card title="Gruppenstruktur" noBody>
          {groups.loading ? (
            <Loading />
          ) : !groups.data?.length ? (
            <EmptyState title="Keine Gruppen" />
          ) : (
            <div style={{ padding: 8 }}>
              {roots.map((group: any) => (
                <div key={group.id}>
                  <GroupButton group={group} selected={selected} onSelect={setSelected} />
                  {childrenOf(group.id).length > 0 && (
                    <div className="tree-indent">
                      {childrenOf(group.id).map((child: any) => (
                        <GroupButton key={child.id} group={child} selected={selected} onSelect={setSelected} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {selected ? (
          <GroupEditor
            groupId={selected}
            catalog={catalog.data}
            allGroups={groups.data ?? []}
            onChanged={() => {
              groups.reload();
              notify('Gruppe gespeichert.', 'success');
            }}
            onDeleted={() => {
              setSelected(null);
              groups.reload();
              notify('Gruppe gelöscht.', 'success');
            }}
          />
        ) : (
          <Card>
            <EmptyState title="Keine Gruppe ausgewählt" />
          </Card>
        )}
      </div>

      {createModal && (
        <CreateGroupModal
          groups={groups.data ?? []}
          onClose={() => setCreateModal(false)}
          onSaved={(id) => {
            setCreateModal(false);
            groups.reload();
            setSelected(id);
            notify('Gruppe angelegt.', 'success');
          }}
        />
      )}
    </div>
  );
}

function GroupButton({
  group,
  selected,
  onSelect,
}: {
  group: any;
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <button
      className={`nav-item ${selected === group.id ? 'active' : ''}`}
      style={{ width: '100%', border: 'none', background: selected === group.id ? undefined : 'transparent', cursor: 'pointer', textAlign: 'left' }}
      onClick={() => onSelect(group.id)}
    >
      <span
        className="icon"
        style={{ width: 8, height: 8, borderRadius: 2, background: group.color ?? 'var(--border-strong)' }}
      />
      <span style={{ flex: 1 }}>
        {group.name}
        <div className="small subtle">
          {group.member_count} Mitglieder · {group.role_count} Rollen
          {group.permission_count > 0 ? ` · ${group.permission_count} Einzelrechte` : ''}
        </div>
      </span>
    </button>
  );
}

function GroupEditor({
  groupId,
  catalog,
  allGroups,
  onChanged,
  onDeleted,
}: {
  groupId: number;
  catalog: any;
  allGroups: any[];
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { can } = useAuth();
  const group = useAsync(() => api.get(`/api/groups/${groupId}`), [groupId]);
  const roles = useAsync(() => api.get('/api/roles').catch(() => []), []);
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);

  const [tab, setTab] = useState<'stamm' | 'mitglieder' | 'rechte'>('stamm');
  const [grants, setGrants] = useState<Grant[]>([]);
  const [form, setForm] = useState<any>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (group.data) {
      setGrants(
        (group.data.permissions ?? []).map((row: any) => ({
          permissionKey: row.permission_key,
          effect: row.effect,
          scope: row.scope,
        })),
      );
      setForm({
        name: group.data.name,
        description: group.data.description ?? '',
        parentGroupId: group.data.parent_group_id ?? '',
      });
    }
  }, [group.data]);

  if (group.loading || !form) return <Card><Loading /></Card>;

  const mayEdit = can('admin.groups.manage');
  const data = group.data;

  const inherited: Record<string, { effect: string; scope: string; source: string }> = {};
  for (const entry of data.inheritedPermissions ?? []) {
    inherited[entry.permission_key] = { effect: entry.effect, scope: entry.scope, source: entry.source };
  }

  const save = async (payload: Record<string, unknown>) => {
    try {
      await api.patch(`/api/groups/${groupId}`, payload);
      group.reload();
      onChanged();
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <Card
      title={data.name}
      subtitle={
        data.ancestors?.length
          ? `Untergruppe von ${data.ancestors.map((entry: any) => entry.name).join(' → ')}`
          : `Schlüssel: ${data.key}`
      }
      actions={
        mayEdit && !data.is_system ? (
          <button
            className="btn sm danger"
            onClick={async () => {
              if (!window.confirm(`Gruppe "${data.name}" wirklich löschen?`)) return;
              await api.delete(`/api/groups/${groupId}`);
              onDeleted();
            }}
          >
            Löschen
          </button>
        ) : null
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'stamm', label: 'Stammdaten & Rollen' },
            { key: 'mitglieder', label: 'Mitglieder', count: data.members?.length },
            { key: 'rechte', label: 'Gruppenrechte', count: grants.length },
          ]}
        />

        {tab === 'stamm' && (
          <div className="stack">
            <div className="form-grid">
              <Field label="Bezeichnung">
                <input value={form.name} disabled={!mayEdit} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </Field>
              <Field label="Übergeordnete Gruppe" hint="Untergruppen erben Rollen und Rechte der übergeordneten Gruppe.">
                <SearchSelect
                  value={String(form.parentGroupId ?? '')}
                  onChange={(wert) => setForm({ ...form, parentGroupId: wert })}
                  options={allGroups
                  .filter((entry: any) => entry.id !== groupId)
                  .map((entry: any) => ({ value: String(entry.id), label: String(entry.name) }))}
                  placeholder="Keine (Wurzelgruppe)"
                  disabled={!mayEdit}
                />
              </Field>
            </div>
            <Field label="Beschreibung">
              <textarea value={form.description} disabled={!mayEdit} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </Field>

            <div>
              <div className="small muted" style={{ marginBottom: 6, fontWeight: 600 }}>
                Rollen dieser Gruppe
              </div>
              <div className="stack" style={{ gap: 6 }}>
                {(roles.data ?? []).map((role: any) => {
                  const active = (data.roles ?? []).some((entry: any) => entry.id === role.id);
                  return (
                    <label className="checkbox-row" key={role.id}>
                      <input
                        type="checkbox"
                        checked={active}
                        disabled={!mayEdit}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...(data.roles ?? []).map((entry: any) => entry.id), role.id]
                            : (data.roles ?? []).map((entry: any) => entry.id).filter((entry: number) => entry !== role.id);
                          void save({ roleIds: next });
                        }}
                      />
                      <span style={{ flex: 1 }}>{role.name}</span>
                      <Badge>{role.permission_count} Rechte</Badge>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="small muted" style={{ marginBottom: 6, fontWeight: 600 }}>
                Objektzuständigkeiten der Gruppe
              </div>
              <div className="row" style={{ gap: 6 }}>
                {(properties.data ?? []).map((property: any) => {
                  const active = (data.properties ?? []).some((entry: any) => entry.id === property.id);
                  return (
                    <button
                      key={property.id}
                      className={`btn sm ${active ? 'active' : ''}`}
                      disabled={!mayEdit}
                      onClick={() => {
                        const next = active
                          ? (data.properties ?? []).map((entry: any) => entry.id).filter((entry: number) => entry !== property.id)
                          : [...(data.properties ?? []).map((entry: any) => entry.id), property.id];
                        void save({ propertyIds: next });
                      }}
                    >
                      {property.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {mayEdit && (
              <div className="row end">
                <button
                  className="btn primary"
                  onClick={() =>
                    save({
                      name: form.name,
                      description: form.description,
                      parentGroupId: form.parentGroupId ? Number(form.parentGroupId) : null,
                    })
                  }
                >
                  Speichern
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'mitglieder' && (
          <div className="stack">
            {(data.members ?? []).length === 0 ? (
              <EmptyState title="Keine Mitglieder" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Funktion</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((member: any) => (
                      <tr key={member.id}>
                        <td>
                          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                            <Avatar firstName={member.first_name} lastName={member.last_name} size="sm" />
                            <div>
                              {member.first_name} {member.last_name}
                              <div className="small subtle">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="small">{member.job_title ?? '–'}</td>
                        <td>
                          <Badge variant={member.is_active ? 'success' : 'danger'}>
                            {member.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </td>
                        <td className="actions">
                          {mayEdit && (
                            <button
                              className="btn sm ghost"
                              onClick={async () => {
                                await api.delete(`/api/groups/${groupId}/members/${member.id}`);
                                group.reload();
                              }}
                            >
                              Entfernen
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {mayEdit && (
              <Field label="Mitglied hinzufügen">
                <select
                  value=""
                  onChange={async (event) => {
                    if (!event.target.value) return;
                    await api.post(`/api/groups/${groupId}/members`, { userIds: [Number(event.target.value)] });
                    group.reload();
                  }}
                >
                  <option value="">Benutzer wählen …</option>
                  {(users.data ?? [])
                    .filter((user: any) => !(data.members ?? []).some((member: any) => member.id === user.id))
                    .map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                </select>
              </Field>
            )}
          </div>
        )}

        {tab === 'rechte' && (
          <div className="stack">
            <div className="alert info small">
              Gruppenrechte übersteuern die Rechte aus Rollen. Sie eignen sich, um einer Gruppe gezielt etwas zu
              erlauben oder – per „Verweigern“ – wieder zu entziehen.
            </div>
            <PermissionMatrix
              catalog={catalog?.permissions ?? []}
              modules={catalog?.modules ?? []}
              grants={grants}
              onChange={setGrants}
              readOnly={!mayEdit}
              inherited={inherited}
            />
            {mayEdit && (
              <div className="row end">
                <button className="btn primary" onClick={() => save({ permissions: grants })}>
                  Gruppenrechte speichern
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function CreateGroupModal({
  groups,
  onClose,
  onSaved,
}: {
  groups: any[];
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const [form, setForm] = useState({ key: '', name: '', description: '', parentGroupId: '' });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title="Gruppe anlegen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={form.key.length < 2 || form.name.length < 2}
            onClick={async () => {
              try {
                const result = await api.post('/api/groups', {
                  ...form,
                  parentGroupId: form.parentGroupId ? Number(form.parentGroupId) : null,
                });
                onSaved(result.id);
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Anlegen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Bezeichnung" required>
          <input
            value={form.name}
            autoFocus
            onChange={(event) =>
              setForm({
                ...form,
                name: event.target.value,
                key: form.key || event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
              })
            }
          />
        </Field>
        <Field label="Schlüssel" required>
          <input value={form.key} onChange={(event) => setForm({ ...form, key: event.target.value })} />
        </Field>
        <Field label="Übergeordnete Gruppe">
          <SearchSelect
            value={String(form.parentGroupId ?? '')}
            onChange={(wert) => setForm({ ...form, parentGroupId: wert })}
            options={(groups ?? []).map((group: any) => ({ value: String(group.id), label: String(group.name) }))}
            placeholder="Keine (Wurzelgruppe)"
          />
        </Field>
        <Field label="Beschreibung">
          <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
